/* Sign-in form validation: email OR phone (E.164).
 *
 * Plan §"OTP login (MVP)": users may enter EITHER an email address OR a
 * phone number in E.164 format. Backend resolves the channel; frontend just
 * needs to pass clean input.
 *
 * Notes:
 *   - We accept loosely formatted phone input (spaces, +420 ###) and
 *     normalize to E.164 before posting.
 *   - Email regex follows the WhatWG-recommended HTML living-spec pattern.
 */

import { z } from 'zod';
import {
  passwordAllowsPhoneLookup,
  type CustomerContactPolicyPayload,
} from 'pi-kiosk-shared';
import i18n from '../../i18n.js';

export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export const E164_REGEX = /^\+[1-9]\d{7,14}$/;

const USERNAME_REGEX = /^[a-zA-Z0-9._-]{2,64}$/;

export type SignInChannel = 'email' | 'phone';

export interface SignInIdentifierResult {
  channel: SignInChannel;
  identifier: string;
}

export type PasswordLoginIdentifierKind = 'email' | 'username' | 'phone';

export type PasswordContactPolicy = Pick<
  CustomerContactPolicyPayload,
  'phoneAllowed'
>;

export function normalizePhoneToE164(raw: string): string | null {
  const cleaned = raw.replace(/[\s().-]/g, '');
  if (!cleaned.startsWith('+') || !E164_REGEX.test(cleaned)) {
    return null;
  }
  return cleaned;
}

/**
 * Password / forgot-password identifier — username/email always;
 * E.164 when policy allows phone (aligned with kiosk G17).
 */
export function classifyPasswordLoginIdentifier(
  raw: string,
  policy: PasswordContactPolicy = { phoneAllowed: false }
): { kind: PasswordLoginIdentifierKind; identifier: string } | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (EMAIL_REGEX.test(trimmed)) {
    return { kind: 'email', identifier: trimmed.toLowerCase() };
  }
  if (passwordAllowsPhoneLookup(policy)) {
    const phone = normalizePhoneToE164(trimmed);
    if (phone !== null) {
      return { kind: 'phone', identifier: phone };
    }
  }
  if (USERNAME_REGEX.test(trimmed)) {
    return { kind: 'username', identifier: trimmed };
  }
  return null;
}

export function classifySignInIdentifier(
  raw: string
): SignInIdentifierResult | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (EMAIL_REGEX.test(trimmed)) {
    return { channel: 'email', identifier: trimmed.toLowerCase() };
  }
  const phone = normalizePhoneToE164(trimmed);
  if (phone !== null) {
    return { channel: 'phone', identifier: phone };
  }
  return null;
}

export const requestOtpSchema = z
  .object({
    identifier: z
      .string()
      .min(1, i18n.t('validation.identifier.required', { ns: 'customer' }))
      .max(254, i18n.t('validation.identifier.tooLong', { ns: 'customer' }))
      .transform((value) => value.trim()),
  })
  .superRefine((data, ctx) => {
    const classified = classifySignInIdentifier(data.identifier);
    if (classified === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['identifier'],
        message: i18n.t('validation.identifier.invalid', { ns: 'customer' }),
      });
    }
  });

export type RequestOtpInput = z.infer<typeof requestOtpSchema>;

// Phase 0 contract stabilization (plan §"Canonical contracts"): canonical
// verify-otp body is `{ jti, code }`. `challengeId` is intentionally removed.
export const verifyOtpSchema = z.object({
  jti: z.string().min(1),
  code: z
    .string()
    .min(1, i18n.t('validation.code.required', { ns: 'customer' }))
    .regex(/^\d{4,8}$/, i18n.t('validation.code.invalid', { ns: 'customer' })),
});

export type VerifyOtpInput = z.infer<typeof verifyOtpSchema>;

export function createLoginPasswordSchema(
  policy: PasswordContactPolicy
): z.ZodType<{ identifier: string; password: string }> {
  return z
    .object({
      identifier: z
        .string()
        .min(1, i18n.t('validation.password.identifierRequired', { ns: 'customer' }))
        .max(254, i18n.t('validation.identifier.tooLong', { ns: 'customer' }))
        .transform((value) => value.trim()),
      password: z
        .string()
        .min(1, i18n.t('validation.password.required', { ns: 'customer' }))
        .max(128, i18n.t('validation.password.tooLong', { ns: 'customer' })),
    })
    .superRefine((data, ctx) => {
      if (classifyPasswordLoginIdentifier(data.identifier, policy) === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['identifier'],
          message: i18n.t('validation.identifier.invalid', { ns: 'customer' }),
        });
      }
    });
}

/** @deprecated Prefer createLoginPasswordSchema(policy) — default email-only. */
export const loginPasswordSchema = createLoginPasswordSchema({
  phoneAllowed: false,
});

export type LoginPasswordInput = z.infer<typeof loginPasswordSchema>;

const registrationConsentsSchema = z.object({
  termsAccepted: z.literal(true, {
    errorMap: () => ({
      message: i18n.t('validation.consents.termsRequired', { ns: 'customer' }),
    }),
  }),
  privacyAccepted: z.literal(true, {
    errorMap: () => ({
      message: i18n.t('validation.consents.privacyRequired', { ns: 'customer' }),
    }),
  }),
  marketing: z.boolean().default(false),
  analytics: z.boolean().default(false),
});

export const registerCustomerSchema = z
  .object({
    email: z
      .string()
      .max(254, i18n.t('validation.identifier.tooLong', { ns: 'customer' }))
      .optional(),
    phone: z
      .string()
      .max(32, i18n.t('validation.identifier.tooLong', { ns: 'customer' }))
      .optional(),
    name: z
      .string()
      .max(120, i18n.t('validation.name.tooLong', { ns: 'customer' }))
      .optional(),
    consents: registrationConsentsSchema,
  })
  .superRefine((data, ctx) => {
    const emailRaw = data.email?.trim() ?? '';
    const phoneRaw = data.phone?.trim() ?? '';
    if (emailRaw.length === 0 && phoneRaw.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: i18n.t('validation.contact.atLeastOne', { ns: 'customer' }),
      });
      return;
    }
    if (emailRaw.length > 0 && !EMAIL_REGEX.test(emailRaw)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: i18n.t('validation.email.invalid', { ns: 'customer' }),
      });
    }
    if (phoneRaw.length > 0 && normalizePhoneToE164(phoneRaw) === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['phone'],
        message: i18n.t('validation.phone.invalid', { ns: 'customer' }),
      });
    }
  });

export type RegisterCustomerInput = z.infer<typeof registerCustomerSchema>;

/** Normalize register form contacts for API (empty → omit). */
export function normalizeRegisterContacts(input: {
  email?: string;
  phone?: string;
}): { email?: string; phone?: string } | null {
  const emailRaw = input.email?.trim() ?? '';
  const phoneRaw = input.phone?.trim() ?? '';
  const email =
    emailRaw.length > 0 && EMAIL_REGEX.test(emailRaw)
      ? emailRaw.toLowerCase()
      : undefined;
  const phone =
    phoneRaw.length > 0 ? (normalizePhoneToE164(phoneRaw) ?? undefined) : undefined;
  if (email === undefined && phone === undefined) {
    return null;
  }
  if (emailRaw.length > 0 && email === undefined) {
    return null;
  }
  if (phoneRaw.length > 0 && phone === undefined) {
    return null;
  }
  return {
    ...(email !== undefined ? { email } : {}),
    ...(phone !== undefined ? { phone } : {}),
  };
}

export const otpConsentsSchema = registrationConsentsSchema;

export const setCredentialsSchema = z
  .object({
    username: z
      .string()
      .max(64, i18n.t('validation.username.tooLong', { ns: 'customer' }))
      .optional(),
    password: z
      .string()
      .min(6, i18n.t('validation.password.minLength', { ns: 'customer' }))
      .max(128, i18n.t('validation.password.tooLong', { ns: 'customer' })),
    confirmPassword: z
      .string()
      .min(1, i18n.t('validation.password.confirmRequired', { ns: 'customer' })),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmPassword'],
        message: i18n.t('validation.password.mismatch', { ns: 'customer' }),
      });
    }
  });

export type SetCredentialsInput = z.infer<typeof setCredentialsSchema>;

export function createForgotPasswordSchema(
  policy: PasswordContactPolicy
): z.ZodType<{ identifier: string }> {
  return z
    .object({
      identifier: z
        .string()
        .min(1, i18n.t('validation.password.identifierRequired', { ns: 'customer' }))
        .max(254, i18n.t('validation.identifier.tooLong', { ns: 'customer' }))
        .transform((value) => value.trim()),
    })
    .superRefine((data, ctx) => {
      if (classifyPasswordLoginIdentifier(data.identifier, policy) === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['identifier'],
          message: i18n.t('validation.identifier.invalid', { ns: 'customer' }),
        });
      }
    });
}

/** @deprecated Prefer createForgotPasswordSchema(policy) — default email-only. */
export const forgotPasswordSchema = createForgotPasswordSchema({
  phoneAllowed: false,
});

export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    token: z.string().min(1, i18n.t('validation.reset.tokenRequired', { ns: 'customer' })),
    password: z
      .string()
      .min(6, i18n.t('validation.password.minLength', { ns: 'customer' }))
      .max(128, i18n.t('validation.password.tooLong', { ns: 'customer' })),
    confirmPassword: z
      .string()
      .min(1, i18n.t('validation.password.confirmRequired', { ns: 'customer' })),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmPassword'],
        message: i18n.t('validation.password.mismatch', { ns: 'customer' }),
      });
    }
  });

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
